import API, { graphqlOperation } from "@aws-amplify/api";
import useAPI from "@toothfairy/shared-api/useApi";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { onUpdateChatPlanner } from "../../../../app/src/graphql/custom";
import {
  onCreateChatMessageByChatIDEvent,
  onUpdateChatMessageByChatIDEvent,
} from "../../../../app/src/graphql/subscriptions";
import { key_id } from "../../../App";
import envConfig from "../../../envConfig";
import chatsHelper from "../../Api/Chats/chatsHelper";
import Common from "../../Api/Common";
import Files from "../../Api/Files";
import Token from "../../Api/Token";
import useAgentStream from "../../Hooks/useAgentStream";
import { user } from "../../Constants";
import { useHistory, useLocation, useParams } from "../../Router";
import AgentScreen from "./AgentScreen";

const AgentScreenContainer = () => {
  const defaultPlaceholderMessageOnLoading = "**Thinking...**";
  const { agentId, workspaceId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const customerId = queryParams.get("tfCustomerId");
  const caseId = queryParams.get("tfCaseId");
  const history = useHistory();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const AppToasty = AppToast.useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [requestedStop, setRequestedStop] = useState(false);
  const [isFirstMessage, setIsFirstMessage] = useState(true);
  const [workspace, setWorkspace] = useState(null);
  const [subscriptionObj, setSubscriptionObject] = useState(null);
  const [subscriptionObj2, setSubscriptionObject2] = useState(null);
  const [subscriptionObj3, setSubscriptionObject3] = useState(null);
  const [lastOptimisticId, setLastOptimisticId] = useState(null);
  const [allowStop, setAllowStop] = useState(true);
  const [isAIStreaming, setIsAIStreaming] = useState(false);
  const [isMessagesLoaded, setIsMessagesLoaded] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [messageIndex, setMessageIndex] = useState(1);
  const [inputText, setInputText] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessageId, setLoadingMessageId] = useState(null);
  const [agentData, setAgentData] = useState(null);
  const [encryptedToken_, setEncryptedToken_] = useState(null);
  const [handedOffToHuman, setHandedOffToHuman] = useState(false);
  const handedOffToHumanRef = useRef();
  const [agentImage, setAgentImage] = useState(null);
  const [agentLogo, setAgentLogo] = useState(null);
  const [waitingForFirstResponse, setWaitingForFirstResponse] = useState(false);
  const [waitingForFirstChunk, setWaitingForFirstChunk] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [fileUrls, setFileUrls] = useState([]);
  // S3 keys of the uploaded doc files, forwarded to the agent with the message
  const [fileKeys, setFileKeys] = useState([]);
  const [imageUrls, setImageUrls] = useState([]);
  const [audioUrls, setAudioUrls] = useState([]);
  const messagesRef = useRef([]);
  const isSSEStreamingRef = useRef(false);
  const { mode } = AppTheme.useTheme();
  const cursor = mode === "light" ? "\u26AB" : "\u26AA";
  const {
    startStream: startSSEStream,
    cancelStream: cancelSSEStream,
    isStreaming: isSSEStreaming,
    connectionStatus: sseConnectionStatus,
    getAccumulatedText,
  } = useAgentStream();
  const {
    data: S3downloadUrlData,
    loading: urldownloadGenerationInProgress,
    apiRequest: S3downloadUrlRequest,
    response: S3downloadResponse,
  } = useAPI(Files.downloadUrlGeneration, envConfig);
  const {
    data: S3downloadUrlDataForWidget,
    loading: urldownloadGenerationInProgressForWidget,
    apiRequest: S3downloadUrlRequestForWidget,
    response: S3downloadResponseForWidget,
  } = useAPI(Files.downloadUrlGeneration, envConfig);
  const fetchAgentImage = async (path, isLargeLogo) => {
    const _url = await S3downloadUrlRequest({
      filename: `imported-image/${workspaceId}/${path}`,
      context: "pdf",
      workspaceid: workspaceId,
    });
    if (isLargeLogo) setAgentLogo(_url?.url);
    else {
      setAgentImage(_url?.url);
      updateFavicon(_url?.url);
    }
  };

  const updateFavicon = (icoUrl) => {
    // Remove existing favicon links
    const existingLinks = document.querySelectorAll("link[rel*='icon']");
    existingLinks.forEach((link) => link.parentNode.removeChild(link));

    // Create and add new favicon link
    const link = document.createElement("link");
    link.type = "image/x-icon";
    link.rel = "icon";
    link.href = icoUrl;
    document.getElementsByTagName("head")[0].appendChild(link);
  };
  const fetchT = async () => {
    const _ = await Token.getState(workspaceId);
    if (_?.data?.w_act == true) {
      const encryptedToken = await Token.getToken(agentId);
      setEncryptedToken_(encryptedToken?.data?.getTokenByAgent);
      key_id(encryptedToken?.data?.getTokenByAgent);
    } else {
      history.push("/error");
    }
  };
  useEffect(() => {
    if (agentData?.icon && workspaceId) {
      fetchAgentImage(agentData?.icon);
    } else {
      setAgentImage(null);
    }
  }, [agentData?.icon, workspaceId]);

  useEffect(() => {
    if (agentData?.largeLogo && workspaceId)
      fetchAgentImage(agentData?.largeLogo, true);
  }, [agentData?.largeLogo, workspaceId]);
  useEffect(() => {
    dispatch("SET_WORKSPACE_ID", workspaceId);
    dispatch("SET_AGENT_ID", agentId);
    if (agentId && workspaceId) fetchT();
  }, [agentId, workspaceId]);
  const setupSession = async () => {
    if (isFirstMessage && encryptedToken_) {
      try {
        const authToken = Common.decrypt(encryptedToken_);
        const [agent] = await Promise.all([
          // change this to reduce load
          chatsHelper.getAgent(agentId, workspaceId, authToken),
        ]);
        if (
          agent?.data?.getAgent?.preventWidgetUsage != false ||
          agent?.data?.getAgent?.mode == "planner"
        ) {
          history.push("/error");
          return;
        }
        setWorkspace({
          id: agent?.data?.getWorkspace?.id,
          customChartingConfig: JSON.parse(
            agent?.data?.getWorkspace?.customChartingConfig
          ),
        });
        setAgentData(agent?.data?.getAgent);
      } catch (error) {
        history.push("/error");
        console.error(error);
      }
    }
  };
  function removeSpecificUnicodeAtEnd(text) {
    const unicodeRegex = /(\u26AB|\u26AA)$/g;
    return text.replace(unicodeRegex, "");
  }
  useEffect(() => {
    const updateMessage = () => {
      setMessages((prevMessages) => {
        let updatedMessages = [...prevMessages];
        updatedMessages[0]["text"] = agentData?.messagesOnLaunch[carouselIndex];
        return updatedMessages;
      });

      // Increment index or loop back to 0
      setCarouselIndex(
        (prevIndex) =>
          (prevIndex + 1) % (agentData?.messagesOnLaunch.length || 1)
      );
    };
    if (
      agentData?.messagesOnLaunch?.length > 0 &&
      agentData?.rotateMessagesOnLaunchInterval &&
      messages?.length == 1
    ) {
      const interval = setInterval(
        updateMessage,
        agentData?.rotateMessagesOnLaunchInterval || 5000
      );

      return () => {
        clearInterval(interval); // Cleanup interval on component unmount
      };
    }
  }, [agentData?.messagesOnLaunch?.length, carouselIndex, messages?.length]);
  const handleFeedback = async (id, humanFeedback) => {
    try {
      const result = await chatsHelper.updateChatMessage(
        {
          id: id,
          humanFeedback: humanFeedback,
        },
        Common.decrypt(encryptedToken_)
      );
      setMessages((prevMessages) => {
        let updatedMessages = [...prevMessages];
        const newMessageIndex = updatedMessages.map((u) => u._id).indexOf(id);
        if (newMessageIndex !== -1) {
          updatedMessages[newMessageIndex] = {
            ...updatedMessages[newMessageIndex],
            humanFeedback: humanFeedback,
          };
        }
        return [...updatedMessages];
      });
      AppToasty.show("Feedback saved", {
        placement: "bottom",
        type: "success",
      });
    } catch (error) {
      console.log(error);
      AppToasty.show("Error saving feedback", {
        placement: "bottom",
        type: "danger",
      });
    }
  };
  useEffect(() => {
    if (agentId && workspaceId && encryptedToken_) setupSession();
  }, [agentId, workspaceId, encryptedToken_]);
  useEffect(() => {
    if (
      messages?.length === 0 &&
      agentData?.id &&
      agentData?.showSplashWhenEmpty !== true
    ) {
      const timer = setTimeout(() => {
        setMessages((prevMessages) => [
          {
            _id: 2,
            text:
              agentData?.messageOnLaunch ||
              "I'm a ToothFairyAI agent, how can I help you?",
            createdAt: new Date().getTime() / 1000,
            user: {
              name: "assistant",
              _id: 2,
              agentName: agentData?.label,
            },
          },
          ...prevMessages,
        ]);
        setMessageIndex((prevIndex) => prevIndex + 1);
      }, 50);

      return () => clearTimeout(timer);
    } else {
      setTimeout(() => {
        setIsMessagesLoaded(true);
      }, 10);
    }
  }, [
    agentData?.messageOnLaunch,
    agentData?.id,
    messageIndex,
    messages?.length,
  ]);

  useEffect(() => {
    let subscriptionObj;
    let subscriptionObj2;
    let subscriptionObj3;
    if (state.chatId) {
      subscriptionObj?.unsubscribe();
      subscriptionObj2?.unsubscribe();
      envConfig.REACT_APP_ENV == "dev" && console.log("_", "subscribing");
      subscriptionObj3 = API.graphql(
        graphqlOperation(onCreateChatMessageByChatIDEvent, {
          chatID: state.chatId,
        })
      ).subscribe({
        next: async (data) => {
          const mainMessageObj =
            data?.value?.data?.onCreateChatMessageByChatIDEvent;

          // Check if message already exists (SSE may have created it first)
          const messageAlreadyExists = messagesRef.current?.find(
            (m) => m._id === mainMessageObj?.id
          );
          if (messageAlreadyExists) {
            envConfig?.REACT_APP_ENV == "dev" &&
              console.log(
                "[Subscription] onCreate skipped - message already exists (created by SSE):",
                mainMessageObj?.id
              );
            return;
          }

          // Skip user message creation events when SSE streaming is active
          if (isSSEStreamingRef.current && mainMessageObj?.role === "user") {
            envConfig?.REACT_APP_ENV == "dev" &&
              console.log(
                "[Subscription] onCreate skipped - SSE user message:",
                mainMessageObj?.id
              );
            return;
          }

          let _agent_ = null;
          const message = mainMessageObj?.text;
          const _agentId = mainMessageObj?.agentID;
          // ISSUE SINCE THE AGENT ID OF THE CREATED OBJECT IS ALWAYS THE PREVIOUS AGENT ID
          if (_agentId != agentId && _agentId != null) {
            const authToken = Common.decrypt(encryptedToken_);
            _agent_ = await chatsHelper.getAgent(
              _agentId,
              workspaceId,
              authToken
            );
            setAgentData(_agent_?.data?.getAgent);
          }
          const newMessage = {
            ...mainMessageObj,
            _id: mainMessageObj.id,
            createdAt: mainMessageObj.creationTime,
            user: {
              id: null,
              _id: handedOffToHumanRef?.current ? 2 : null,
              name: "assistant",
              agentName: handedOffToHumanRef?.current
                ? "Human"
                : agentData?.label,
            },
            text: `${message}`,
            loading: false,
            humanFeedback: mainMessageObj?.humanFeedback,
            humanFeedbackType: mainMessageObj?.humanFeedbackType,
            humanFeedbackSubType: mainMessageObj?.humanFeedbackSubType,
            humanFeedbackComment: mainMessageObj?.humanFeedbackComment,
            metadata:
              typeof mainMessageObj.metadata === "string"
                ? JSON.parse(mainMessageObj.metadata)
                : mainMessageObj.metadata,
            callbackMetadata:
              typeof mainMessageObj.callbackMetadata === "string"
                ? JSON.parse(mainMessageObj.callbackMetadata)
                : mainMessageObj.callbackMetadata,
          };
          setTimeout(() => {
            setMessages((prevMessages) => {
              let updatedMessages = [...prevMessages];
              if (updatedMessages?.find((u) => u._id == newMessage?._id))
                return updatedMessages;
              updatedMessages.push(newMessage);
              // find loading message and remove it
              const loadingMessageIndex = updatedMessages.indexOf(
                loadingMessageId
              );
              // find the last index of the loading message just by looking at the loading property

              const loadingMessageIndex2 = updatedMessages.findIndex(
                (u) => u.loading
              );
              if (loadingMessageIndex2 !== -1) {
                updatedMessages[loadingMessageIndex2] = {
                  ...updatedMessages[loadingMessageIndex2],
                  user: {
                    ...updatedMessages[loadingMessageIndex2].user,
                    agentName: _agent_?.data?.getAgent?.label,
                  },
                };
              }
              // sort messages by createdAt
              updatedMessages = updatedMessages.sort((a, b) => {
                return new Date(a.createdAt) - new Date(b.createdAt);
              });
              return [...updatedMessages];
            });
          }, 1000);
        },
      });
      subscriptionObj2 = API.graphql(
        graphqlOperation(onUpdateChatMessageByChatIDEvent, {
          chatID: state.chatId,
        })
      ).subscribe({
        next: async (data) => {
          const mainMessageObj =
            data?.value?.data?.onUpdateChatMessageByChatIDEvent;
          envConfig?.REACT_APP_ENV == "dev" &&
            console.log(
              "_",
              data?.value?.data?.onUpdateChatMessageByChatIDEvent,
              { isAIStreaming },
              { loadingMessageId }
            );

          const message =
            data?.value?.data?.onUpdateChatMessageByChatIDEvent?.text;

          // ALWAYS process callback metadata updates (suggestions/nextQuestion)
          // These come after streaming completes and need to be shown regardless of SSE state
          if (
            mainMessageObj.status === "fulfilled" &&
            (mainMessageObj?.callbackMetadata?._type == "suggestion" ||
              mainMessageObj?.callbackMetadata?._type == "nextQuestion")
          ) {
            setMessages((prevMessages) => {
              let updatedMessages = [...prevMessages];
              const callbackMessageIndex = updatedMessages
                .map((u) => u._id)
                .indexOf(mainMessageObj?.id);
              if (callbackMessageIndex !== -1) {
                updatedMessages[callbackMessageIndex] = {
                  ...updatedMessages[callbackMessageIndex],
                  callbackMetadata:
                    typeof mainMessageObj.callbackMetadata === "string"
                      ? JSON.parse(mainMessageObj.callbackMetadata)
                      : mainMessageObj.callbackMetadata,
                };
              }
              return [...updatedMessages];
            });
          }

          // When SSE is active, only process callback metadata (above)
          // SSE handles all text streaming
          if (isSSEStreamingRef.current) {
            return;
          }

          if (requestedStop) return;
          if (mainMessageObj.status === "fulfilled" && isAIStreaming) {
            envConfig.REACT_APP_ENV == "dev" && console.log("_", "fulfilled");
            if (loadingMessageId === mainMessageObj.id) {
              setIsAIStreaming(false);
              setLoadingMessageId(null);
            }
            setMessages((prevMessages) => {
              let updatedMessages = [...prevMessages];
              const loadingMessageIndex = updatedMessages
                .map((u) => u._id)
                .indexOf(mainMessageObj.id);
              if (loadingMessageIndex !== -1) {
                updatedMessages[loadingMessageIndex] = {
                  ...updatedMessages[loadingMessageIndex],
                  text: removeSpecificUnicodeAtEnd(message),
                  loading: false,
                  metadata:
                    typeof mainMessageObj.metadata === "string"
                      ? JSON.parse(mainMessageObj.metadata)
                      : mainMessageObj.metadata,
                  callbackMetadata:
                    typeof mainMessageObj.callbackMetadata === "string"
                      ? JSON.parse(mainMessageObj.callbackMetadata)
                      : mainMessageObj.callbackMetadata,
                };
              }
              return [...updatedMessages];
            });
          } else if (
            (mainMessageObj.text !== "**...**" &&
              mainMessageObj.text !== agentData?.placeholderMessageOnLoading &&
              mainMessageObj.isAI === true) ||
            (loadingMessageId == null && isAIStreaming)
          ) {
            envConfig.REACT_APP_ENV == "dev" &&
              console.log("loadingMessageId", mainMessageObj.id);
            // update the last message in the chat with role assistant to the new message
            setMessages((prevMessages) => {
              let updatedMessages = [...prevMessages];
              const loadingMessageIndex = updatedMessages
                .map((u) => u._id)
                .indexOf(mainMessageObj.id);
              if (loadingMessageIndex !== -1) {
                updatedMessages[loadingMessageIndex] = {
                  ...updatedMessages[loadingMessageIndex],
                  text: `${message}${isAIStreaming ? " " + cursor : ""}`,
                  loading: false,
                };
              }
              return [...updatedMessages];
            });
          }
        },
        error: (error) => {
          console.log(`Error with subscription:`, error);
        },
      });
      subscriptionObj = API.graphql(
        graphqlOperation(onUpdateChatPlanner, {
          id: state.chatId,
        })
      ).subscribe({
        next: async (data) => {
          let chatObj = data?.value?.data?.onUpdateChat;
          if (chatObj?.id != state.chatId) return;
          setHandedOffToHuman(chatObj?.handedOffToHuman == true);
          handedOffToHumanRef.current = chatObj?.handedOffToHuman == true;
        },
      });
      setSubscriptionObject(subscriptionObj);
      setSubscriptionObject2(subscriptionObj2);
      setSubscriptionObject3(subscriptionObj3);
    }

    return () => {
      if (subscriptionObj) {
        envConfig.REACT_APP_ENV == "dev" && console.log("_", "unsubscribing");
        // subscriptionObj.unsubscribe();
        subscriptionObj2.unsubscribe();
        subscriptionObj3.unsubscribe();
      }
    };
  }, [state?.chatId, loadingMessageId, isAIStreaming, encryptedToken_]);
  useEffect(() => {
    handedOffToHumanRef.current = handedOffToHuman;
  }, [handedOffToHuman]);
  // Keep refs in sync with state
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  useEffect(() => {
    isSSEStreamingRef.current = isSSEStreaming;
  }, [isSSEStreaming]);
  const onCancelGeneration = async () => {
    // Handle SSE streaming cancellation
    if (isSSEStreaming) {
      cancelSSEStream();
      setIsAIStreaming(false);
      setAllowStop(false);
      setWaitingForFirstResponse(false);
      setWaitingForFirstChunk(false);
      // Update message status to cancelled
      if (loadingMessageId) {
        await chatsHelper.updateChatMessage(
          {
            id: loadingMessageId,
            status: "cancelled",
          },
          Common.decrypt(encryptedToken_)
        );
        // Update local message state with accumulated text
        const accumulatedText = getAccumulatedText();
        if (accumulatedText) {
          setMessages((prevMessages) => {
            const messageIndex = prevMessages.findIndex(
              (m) => m._id === loadingMessageId
            );
            if (messageIndex === -1) return prevMessages;
            const updatedMessages = [...prevMessages];
            updatedMessages[messageIndex] = {
              ...updatedMessages[messageIndex],
              text: removeSpecificUnicodeAtEnd(accumulatedText),
              loading: false,
            };
            return updatedMessages;
          });
        }
      }
      await chatsHelper.updateChat(
        state?.chatId,
        [
          { property: "isAIReplying", value: false },
          { property: "activeMessageForAI", value: null },
        ],
        Common.decrypt(encryptedToken_)
      );
      setLoadingMessageId(null);
      setTimeout(() => {
        setAllowStop(true);
      }, 1000);
      return;
    }

    // Original GraphQL subscription cancellation
    subscriptionObj?.unsubscribe();
    subscriptionObj2?.unsubscribe();
    subscriptionObj3?.unsubscribe();
    setRequestedStop(true);
    setAllowStop(false);
    setIsAIStreaming(false);
    setIsLoading(false);

    await chatsHelper.updateChat(
      state?.chatId,
      [
        {
          property: "isAIReplying",
          value: false,
        },
        {
          property: "activeMessageForAI",
          value: null,
        },
      ],
      Common.decrypt(encryptedToken_)
    );
    await chatsHelper.updateChatMessage(
      {
        id: loadingMessageId,
        status: "cancelled",
      },
      Common.decrypt(encryptedToken_)
    );
    setLoadingMessageId(null);
    setTimeout(() => {
      setAllowStop(true);
    }, [1000]);
  };
  // Helper function to update messages while SSE streaming
  const updateMessagesWhileSSEStreaming = useCallback(
    (chunk, messageId, accumulatedText) => {
      setMessages((prevMessages) => {
        const messageIndex = prevMessages.findIndex((m) => m._id === messageId);
        if (messageIndex === -1) return prevMessages;

        const updatedMessages = [...prevMessages];
        const currentMessage = updatedMessages[messageIndex];

        updatedMessages[messageIndex] = {
          ...currentMessage,
          text:
            accumulatedText + (isSSEStreamingRef.current ? " " + cursor : ""),
          loading: false,
        };

        return updatedMessages;
      });
    },
    [cursor]
  );

  let chatID;
  const fetchData = async (message) => {
    try {
      const optimisticMessageId = Math.random().toString(36).substring(7);
      setLastOptimisticId(optimisticMessageId);
      setMessages((previousMessages) => [
        ...previousMessages,
        {
          _id: optimisticMessageId,
          text: message,
          createdAt: new Date().getTime() / 1000,
          user: {
            name: "user",
          },
          loading: false,
        },
      ]);
      let chatID;
      if (isFirstMessage) {
        const authToken = Common.decrypt(encryptedToken_);
        const chatResult = await chatsHelper.createChat(
          {
            primaryRole: agentId,
            workspaceID: workspaceId,
            allowMultipleRoles: false,
            creationTime: Math.floor(new Date().getTime() / 1000),
            visibility: "widget",
            name: "New Chat",
            customerId,
            caseId,
          },
          authToken
        );
        chatID = chatResult?.data?.createChat?.id;
        dispatch("SET_CHAT_ID", chatID);
        setIsFirstMessage(false);
      }
      if (handedOffToHuman) {
        const promises = [
          chatsHelper.createChatMessage({
            chatID: state.chatId || chatID,
            text: message,
            role: "user",
            userID: user._id,
            isAI: false,
            creationTime: Math.floor(new Date().getTime() / 1000),
            workspaceID: workspaceId,
            files: fileKeys?.length ? fileKeys : null,
          }),
        ];
        const [result1] = await Promise.all(promises);
        setMessages((previousMessages) => {
          const updatedMessages = [...previousMessages];
          // find index of the optimistic message
          const optimisticMessageIndex = updatedMessages
            .map((u) => u._id)
            .indexOf(optimisticMessageId);
          // replace the optimistic message with the message from the server
          updatedMessages[optimisticMessageIndex] = {
            ...updatedMessages[optimisticMessageIndex],
            _id: result1?.data?.createChatMessage?.id,
          };
          return updatedMessages;
        });
      } else {
        // SSE streaming approach - backend creates BOTH user and assistant messages
        // Add optimistic user message to UI (already done above)

        setAllowStop(true);
        setIsAIStreaming(true);
        setRequestedStop(false);
        setWaitingForFirstResponse(true);
        setWaitingForFirstChunk(false);

        let assistantMessageId = null;
        const currentChatId = state.chatId || chatID;

        startSSEStream(
          {
            chatid: currentChatId,
            workspaceid: workspaceId,
            token: Common.decrypt(encryptedToken_),
            messages: [
              {
                text: message,
                role: "user",
                userID: user?._id,
                files: fileKeys?.length ? fileKeys : null,
              },
            ],
            agentid: agentData?.id,
          },
          {
            onToken: (chunk, messageId, accumulatedText) => {
              envConfig?.REACT_APP_ENV == "dev" &&
                console.log("[SSE] onToken:", {
                  chunkLength: chunk?.length,
                  messageId,
                  accumulatedTextLength: accumulatedText?.length,
                });
              // First chunk received, no longer waiting
              setWaitingForFirstResponse(false);
              setWaitingForFirstChunk(false);
              // If this is the first token and we don't have a message in UI yet, create it
              if (messageId && !assistantMessageId) {
                assistantMessageId = messageId;
                setLoadingMessageId(messageId);
                // Add assistant message placeholder to UI
                setMessages((previousMessages) => {
                  // Check if message already exists
                  if (previousMessages.find((m) => m._id === messageId)) {
                    return previousMessages;
                  }
                  let f_date = new Date();
                  f_date.setSeconds(f_date.getSeconds() + 1);
                  return [
                    ...previousMessages,
                    {
                      _id: messageId,
                      text: null,
                      createdAt: f_date.getTime() / 1000,
                      user: {
                        _id: 2,
                        name: "assistant",
                        agentName: agentData?.label,
                      },
                      loading: true,
                    },
                  ];
                });
              }
              updateMessagesWhileSSEStreaming(
                chunk,
                messageId || assistantMessageId,
                accumulatedText
              );
            },
            onMessage: (data) => {
              envConfig?.REACT_APP_ENV == "dev" &&
                console.log("[SSE] onMessage:", data);
              // Handle chat_created event - backend created the assistant message
              if (data.type === "chat_created" && data.message_id) {
                assistantMessageId = data.message_id;
                setLoadingMessageId(data.message_id);
                // Got first response with message_id, now waiting for first chunk
                setWaitingForFirstResponse(false);
                setWaitingForFirstChunk(true);
                // Add assistant message placeholder to UI
                setMessages((previousMessages) => {
                  // Check if message already exists
                  if (previousMessages.find((m) => m._id === data.message_id)) {
                    return previousMessages;
                  }
                  let f_date = new Date();
                  f_date.setSeconds(f_date.getSeconds() + 1);
                  return [
                    ...previousMessages,
                    {
                      _id: data.message_id,
                      text: null,
                      createdAt: f_date.getTime() / 1000,
                      user: {
                        _id: 2,
                        name: "assistant",
                        agentName: agentData?.label,
                      },
                      loading: true,
                    },
                  ];
                });
              }
              // Handle fulfilled status - mark streaming as complete
              if (data.status === "fulfilled" && data.type === "message") {
                envConfig?.REACT_APP_ENV == "dev" &&
                  console.log("[SSE] fulfilled message received:", {
                    message_id: data.message_id,
                    metadata: data.metadata,
                    callbackMetadata: data.callbackMetadata,
                  });
                setIsAIStreaming(false);
                setAllowStop(false);
                setLoadingMessageId(null);
                // Update message to remove cursor and finalize
                if (data.message_id || assistantMessageId) {
                  const msgId = data.message_id || assistantMessageId;
                  setMessages((prevMessages) => {
                    const messageIndex = prevMessages.findIndex(
                      (m) => m._id === msgId
                    );
                    if (messageIndex === -1) return prevMessages;

                    const updatedMessages = [...prevMessages];
                    const currentText =
                      updatedMessages[messageIndex].text || "";
                    updatedMessages[messageIndex] = {
                      ...updatedMessages[messageIndex],
                      text: removeSpecificUnicodeAtEnd(currentText),
                      loading: false,
                      metadata: data.metadata
                        ? typeof data.metadata === "string"
                          ? JSON.parse(data.metadata)
                          : data.metadata
                        : updatedMessages[messageIndex].metadata,
                      callbackMetadata: data.callbackMetadata
                        ? typeof data.callbackMetadata === "string"
                          ? JSON.parse(data.callbackMetadata)
                          : data.callbackMetadata
                        : updatedMessages[messageIndex].callbackMetadata,
                    };
                    return updatedMessages;
                  });
                }
                // Update chat state
                chatsHelper.updateChat(
                  currentChatId,
                  [
                    { property: "isAIReplying", value: false },
                    { property: "activeMessageForAI", value: null },
                  ],
                  Common.decrypt(encryptedToken_)
                );
              }
            },
            onComplete: async (finalText) => {
              envConfig?.REACT_APP_ENV == "dev" &&
                console.log("[SSE] onComplete:", {
                  finalTextLength: finalText?.length,
                  assistantMessageId,
                });
              setIsAIStreaming(false);
              setAllowStop(false);
              setWaitingForFirstResponse(false);
              setWaitingForFirstChunk(false);
              // Update message in state with final text (without cursor)
              if (assistantMessageId) {
                setMessages((prevMessages) => {
                  const messageIndex = prevMessages.findIndex(
                    (m) => m._id === assistantMessageId
                  );
                  if (messageIndex === -1) return prevMessages;
                  const updatedMessages = [...prevMessages];
                  updatedMessages[messageIndex] = {
                    ...updatedMessages[messageIndex],
                    text: removeSpecificUnicodeAtEnd(finalText),
                    loading: false,
                  };
                  return updatedMessages;
                });
              }
              // Update chat state
              await chatsHelper.updateChat(
                currentChatId,
                [
                  { property: "isAIReplying", value: false },
                  { property: "activeMessageForAI", value: null },
                ],
                Common.decrypt(encryptedToken_)
              );
            },
            onError: async (error) => {
              envConfig?.REACT_APP_ENV == "dev" &&
                console.log("[SSE] onError:", error);
              console.error("SSE stream error:", error);
              setIsAIStreaming(false);
              setAllowStop(false);
              setWaitingForFirstResponse(false);
              setWaitingForFirstChunk(false);
              AppToasty.show(
                "Error processing your request, please try again later",
                {
                  placement: "bottom",
                  type: "danger",
                }
              );
              await chatsHelper.updateChat(
                currentChatId,
                [
                  { property: "isAIReplying", value: false },
                  { property: "activeMessageForAI", value: null },
                ],
                Common.decrypt(encryptedToken_)
              );
            },
          }
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      // remove the loading message by checking the loading property
      // setMessages((previousMessages) =>
      //   previousMessages.filter((m) => !m.loading)
      // );
    }
  };
  // from messages get the last message from the assistant handling the case where there are no messages yet or no messages from the assistant
  const lastMessageFromAssistant =
    messages?.filter((u) => u?.user?.name === "assistant")?.slice(-1)[0] || {};

  useEffect(() => {
    if (
      messages?.filter((u) => u?.loading === true).length > 0 &&
      !isAIStreaming
    ) {
      // Update the last message's loading status and add the reply
      setMessages((previousMessages) => {
        const updatedMessages = previousMessages.slice();
        updatedMessages[0].loading = false;
        // updatedMessages[0].text = "Something went wrong, please try again";
        return updatedMessages;
      });
    }
  }, [messages?.length, isAIStreaming]);
  const onImageUploaded = (path) => {
    setImageUrls((prev) => [...prev, path]);
  };
  const onAudioUploaded = (path) => {
    setAudioUrls((prev) => [...prev, path]);
  };
  const onVideoUploaded = (path) => {
    setVideoUrl(null);
  };
  const onFileUploaded = (path) => {
    setFileUrl(path);
  };
  const onFilesUploaded = (uploadedFilesUrls, presignedFilesUrls, removedIndex, type) => {
    if (removedIndex != null && type) {
      if (type === "image") {
        setImageUrls((prev) => prev.filter((_, i) => i !== removedIndex));
      } else if (type === "audio") {
        setAudioUrls((prev) => prev.filter((_, i) => i !== removedIndex));
      } else {
        setFileUrls((prev) => prev.filter((_, i) => i !== removedIndex));
        setFileKeys((prev) => prev.filter((_, i) => i !== removedIndex));
      }
    } else {
      if (presignedFilesUrls) {
        // Functional-append: consecutive async uploads (e.g. rapid clipboard
        // pastes) must not replace the previously added files.
        setFileUrls((prev) => [...(prev || []), ...presignedFilesUrls]);
        setFileKeys((prev) => [...(prev || []), ...uploadedFilesUrls]);
      }
    }
  };

  const handleSend = (message) => {
    if (message.trim().length === 0) return;
    setIsLoading(true);
    fetchData(message);
    // Attachments ride with this message only - clear so they are not
    // re-sent with every subsequent message (mirrors the app containers)
    setFileUrls([]);
    setFileKeys([]);
    setImageUrls([]);
    setAudioUrls([]);
  };
  const onQuestionPress = (question) => {
    if (!isLoading) handleSend(question);
  };
  const onLanguageChange = (language) => {
    dispatch("SET_CONV_LANG", language);
  };

  // change the tab title to the agent title
  useEffect(() => {
    if (agentData?.label) document.title = agentData?.label;
  }, [agentData?.label]);

  return (
    <Suspense fallback={<div>Loading...</div>}>
      {agentData?.id && urldownloadGenerationInProgress == false ? (
        <AgentScreen
          S3downloadUrlRequest={S3downloadUrlRequestForWidget}
          onQuestionPress={onQuestionPress}
          onLanguageChange={onLanguageChange}
          messages={messages}
          setMessages={setMessages}
          user={user}
          handleSend={handleSend}
          isMessagesLoaded={isMessagesLoaded}
          languages={state.languages}
          selectedLanguage={state.selectedLanguage}
          isAIStreaming={isAIStreaming}
          onCancel={onCancelGeneration}
          inputText={inputText}
          setInputText={setInputText}
          loadingMessageId={loadingMessageId}
          isLastMessageEmpty={lastMessageFromAssistant?.text == ""}
          allowFeedback={agentData?.allowFeedback}
          handleFeedback={handleFeedback}
          isLoading={isLoading}
          showAgentName={agentData?.showAgentName}
          placeholder={agentData?.placeholderInputMessage || "Ask me anything"}
          agentTitle={agentData?.label}
          textToDisplayWhileLoading={
            agentData?.placeholderMessageOnLoading ||
            defaultPlaceholderMessageOnLoading
          }
          voiceSensitivity={agentData?.voiceSensitivity || "medium"}
          // autoVoiceMode={agentData?.autoVoiceMode}
          autoVoiceMode={true}
          allowAudiosUpload={agentData?.allowAudiosUpload}
          allowImagesUpload={agentData?.allowImagesUpload}
          allowVideosUpload={agentData?.allowVideosUpload}
          allowDocsUpload={agentData?.allowDocsUpload}
          onImageUploaded={onImageUploaded}
          onAudioUploaded={onAudioUploaded}
          onVideoUploaded={onVideoUploaded}
          onFileUploaded={onFileUploaded}
          onFilesUploaded={onFilesUploaded}
          videoUrl={videoUrl}
          setVideoUrl={setVideoUrl}
          imageUrl={imageUrl}
          setImageUrl={setImageUrl}
          audioUrl={audioUrl}
          setAudioUrl={setAudioUrl}
          fileUrl={fileUrl}
          setFileUrl={setFileUrl}
          fileUrls={fileUrls}
          setFileUrls={setFileUrls}
          imageUrls={imageUrls}
          audioUrls={audioUrls}
          showSplash={agentData?.showSplashWhenEmpty}
          splashTitle={agentData?.messageOnLaunch}
          showDarkMode={
            agentData?.forcedTheme != "light" &&
            agentData?.forcedTheme != "dark"
          }
          coder={true}
          isAgentFetched={agentData?.label != null}
          disclaimer={agentData?.disclaimer}
          agentImage={agentImage}
          agentLogo={agentLogo}
          agentColor={mode === "dark" ? agentData?.darkColor : agentData?.color}
          showToolCallsDetailsInWidget={
            agentData?.showToolCallsDetailsInWidget === true
          }
          logoBackground={
            mode === "dark"
              ? agentData?.darkLogoBackground
              : agentData?.logoBackground
          }
          showTimeForResponse={agentData?.showTimeForResponse}
          showDetectedLanguage={agentData?.showDetectedLanguage}
          quickQuestions={agentData?.quickQuestions
            ?.split(";")
            ?.map((u) => u?.trim()?.replace(/\s+/g, " "))
            ?.filter((u) => u != "")}
          allowStop={allowStop}
          workspaceId={workspaceId}
          workspace={workspace}
          agentData={agentData}
          handedOffToHuman={handedOffToHuman}
          encryptedToken_={encryptedToken_}
          waitingForFirstChunk={waitingForFirstChunk}
          waitingForFirstResponse={waitingForFirstResponse}
        />
      ) : (
        <div className="tf-loader"></div>
      )}
    </Suspense>
  );
};

export default AgentScreenContainer;
